import React, { useState } from "react";
import axios from "axios";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { API_get_store_team_member_weekly_info } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { formatDate } from "../../requests/utilities/helpers";
import PrintShell, {
  SettingsGroup,
  OptionToggle,
  MultiSelect,
  PreviewEmptyState,
  SegmentedControl,
} from "./print/PrintShell";
import WeeklyScheduleSheet, { COLOR_MODES } from "./print/WeeklyScheduleSheet";

const date_options = { weekday: "short", month: "short", day: "numeric" };

function PrintModeAdvanced(props) {
  const dow_nums = [0, 1, 2, 3, 4, 5, 6];
  const day_names = dow_nums.map((dow) =>
    props.addDays(props.week_start_date, dow).toLocaleDateString("en-us", date_options)
  );
  const weekLabel = `${day_names[0]} – ${day_names[6]}`;

  const [isLoading, setIsLoading] = useState(false);
  const [isStale, setIsStale] = useState(true);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState(null);

  const [selectedStores, setSelectedStores] = useState([props.store_id]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showTotalHours, setShowTotalHours] = useState(true);
  const [showIndividualHours, setShowIndividualHours] = useState(true);
  const [colorMode, setColorMode] = useState("time");
  const [showUnavailability, setShowUnavailability] = useState(true);
  const [showRole, setShowRole] = useState(true);
  const [showBreaks, setShowBreaks] = useState(false);
  const [showOpenShifts, setShowOpenShifts] = useState(true);
  const [showNotes, setShowNotes] = useState(true);
  const [teamMembersByStore, setTeamMembersByStore] = useState({});
  const [openShiftsByStore, setOpenShiftsByStore] = useState({});

  const storeOptions = (props.storeNames || []).map((s) => ({
    value: s.id,
    label: s.store_name,
  }));
  const roleOptions = (props.role_names || []).map((r) => ({
    value: r.role_name,
    label: r.role_name,
  }));
  const storeNameFor = (id) =>
    props.storeNames?.find((s) => s.id === Number(id))?.store_name || "Location";

  // Selection changes invalidate the fetched data; display toggles don't.
  const changeStores = (next) => {
    setSelectedStores(next);
    setIsStale(true);
  };
  const changeRoles = (next) => {
    setSelectedRoles(next);
    setIsStale(true);
  };

  const handleGenerate = () => {
    setIsLoading(true);
    setError(null);
    const request_data = {
      stores: selectedStores,
      roles:
        selectedRoles.length === 0
          ? props.role_names?.map((r) => r.role_name)
          : selectedRoles,
      date_start: formatDate(props.week_start_date),
      include_open_shifts: true,
    };
    axios
      .post(
        API_get_store_team_member_weekly_info + props.store_id,
        request_data,
        getDefaultAxiosConfig()
      )
      .then((res) => {
        const grouped = (res.data?.team_members || []).reduce((acc, row) => {
          (acc[row.store] = acc[row.store] || []).push(row);
          return acc;
        }, {});
        setTeamMembersByStore(grouped);
        setOpenShiftsByStore(res.data?.open_shifts || {});
        setIsStale(false);
        setHasGenerated(true);
      })
      .catch((err) => {
        console.error("Error fetching schedule data:", err);
        setError("Couldn't load those schedules. Please try again.");
      })
      .finally(() => setIsLoading(false));
  };

  const storeHasOpenShifts = (storeId) =>
    Object.values(openShiftsByStore[storeId] || {}).some((day) => day.length > 0);

  // A store can come back with open shifts but no team-member rows (new or
  // understaffed location, or a role filter nobody's primary role matches).
  // It still needs a table or its open shifts silently vanish.
  const storeIdsToRender = [
    ...new Set([
      ...Object.keys(teamMembersByStore),
      ...(showOpenShifts
        ? Object.keys(openShiftsByStore).filter(storeHasOpenShifts)
        : []),
    ]),
  ].sort((a, b) => Number(a) - Number(b));

  const canPrint = hasGenerated && !isStale && storeIdsToRender.length > 0;

  const settings = (
    <>
      <SettingsGroup title="Locations">
        <MultiSelect
          value={selectedStores}
          onChange={changeStores}
          options={storeOptions}
          placeholder="Choose locations"
          selectAll
        />
      </SettingsGroup>

      <SettingsGroup title="Roles">
        <MultiSelect
          value={selectedRoles}
          onChange={changeRoles}
          options={roleOptions}
          placeholder="All roles"
        />
      </SettingsGroup>

      <div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isLoading || selectedStores.length === 0}
          className="w-full h-9 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border bg-white border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <LoadSpinner />
          ) : hasGenerated ? (
            "Regenerate schedules"
          ) : (
            "Generate schedules"
          )}
        </button>
        {hasGenerated && isStale && !isLoading && (
          <p className="text-[11px] text-amber-700 mt-1.5">
            Locations or roles changed. Regenerate to update the preview.
          </p>
        )}
        {error && <p className="text-[11px] text-red-600 mt-1.5">{error}</p>}
      </div>

      <SettingsGroup title="Hours">
        <OptionToggle
          label="Hours per person"
          checked={showIndividualHours}
          onChange={setShowIndividualHours}
        />
        <OptionToggle
          label="Daily and weekly totals"
          checked={showTotalHours}
          onChange={setShowTotalHours}
        />
      </SettingsGroup>

      <SettingsGroup title="Color shifts by">
        <SegmentedControl
          value={colorMode}
          onChange={setColorMode}
          options={COLOR_MODES}
        />
      </SettingsGroup>

      <SettingsGroup title="Include">
        <OptionToggle
          label="Open shifts"
          checked={showOpenShifts}
          onChange={setShowOpenShifts}
        />
        <OptionToggle
          label="Main role under name"
          checked={showRole}
          onChange={setShowRole}
        />
        <OptionToggle
          label="Unavailability and time off"
          checked={showUnavailability}
          onChange={setShowUnavailability}
        />
        <OptionToggle
          label="Break times"
          checked={showBreaks}
          onChange={setShowBreaks}
        />
        <OptionToggle
          label="Notes"
          hint="Shown under this location's schedule"
          checked={showNotes}
          onChange={setShowNotes}
          disabled={!props.notes?.length}
        />
      </SettingsGroup>
    </>
  );

  const options = {
    showHours: showIndividualHours,
    showTotals: showTotalHours,
    colorMode,
    showUnavailability,
    showRole,
    showBreaks,
    showOpenShifts,
  };

  return (
    <PrintShell
      title="Print multiple locations"
      subtitle={weekLabel}
      documentTitle={`Schedules ${weekLabel}`}
      onClose={props.onClose}
      modeSwitcher={props.modeSwitcher}
      loading={props.scheduleLoading}
      onBack={() => props.setShowAdvancedPrintMode(false)}
      backLabel="Single location"
      settings={settings}
      canPrint={canPrint}
    >
      {storeIdsToRender.length === 0 ? (
        <PreviewEmptyState
          title={
            hasGenerated
              ? "Nothing to print for these settings"
              : "Choose locations and roles, then generate"
          }
          body={
            hasGenerated
              ? "No team members or open shifts matched the selected roles."
              : "Each location prints on its own page."
          }
        />
      ) : (
        storeIdsToRender.map((storeId, i) => (
          <div
            key={storeId}
            className={i > 0 ? "print-page-break mt-10 pt-10 border-t border-dashed border-slate-300" : ""}
          >
            <WeeklyScheduleSheet
              title={storeNameFor(storeId)}
              subtitle={weekLabel}
              dayNames={day_names}
              employees={teamMembersByStore[storeId] || []}
              openShiftsByDay={openShiftsByStore[storeId]}
              notes={props.notes}
              roleNames={props.role_names}
              options={{
                ...options,
                // Notes belong to the home store only.
                showNotes: showNotes && Number(storeId) === Number(props.store_id),
              }}
            />
          </div>
        ))
      )}
    </PrintShell>
  );
}

export default PrintModeAdvanced;
